<div class="voucher-selection-content" [formGroup]="form">
    <div class="voucher-workspace">
        <div class="voucher-description vstack gap-2">
            <h5 class="voucher-title fw-semibold mb-0">{{ voucher.displayName }}</h5>
            @if (voucher.description) {
                <div class="voucher-description-content vstack gap-2">
                    <div class="voucher-description-text quill-flush text-body-secondary" [class.voucher-description-collapsed]="!descriptionShowMore" (focusin)="expandDescription()">
                        <quill-view [content]="voucher.description" (onEditorCreated)="observeDescription($event)"></quill-view>
                    </div>
                    @if (voucherDescriptionCanTruncate) {
                        <button type="button" class="voucher-read-more btn btn-link p-0 text-body" [attr.aria-expanded]="descriptionShowMore" (click)="toggleDescription()">
                            {{ descriptionShowMore ? 'Read less' : 'Read more' }}
                        </button>
                    }
                </div>
            }
        </div>
        <div class="voucher-options vstack gap-3">
            @if (voucher.isOnSale) {
                <div class="voucher-selectors">
                    <div class="form-group">
                        <label for="voucherSku" class="form-label fw-semibold">Select Voucher Type</label>
                        <select
                            id="voucherSku"
                            class="form-select"
                            formControlName="skuId"
                            [class.is-invalid]="skuIdControl?.touched && skuIdControl?.invalid"
                        >
                            @for (skuOption of skuOptions; track skuOption.sku.id) {
                                <option [value]="skuOption.sku.id">
                                    {{ null | currencyUnit : skuOption.amount : venue.currencyCode }} Voucher
                                    @if (skuOption.priceDiffersFromAmount) {
                                        (Price: {{ null | currencyUnit : skuOption.price : venue.currencyCode }})
                                    }
                                </option>
                            }
                        </select>
                        <div class="small text-body-secondary mt-1">
                            From {{ null | currencyUnit : lowestPricedSKUPrice : venue.currencyCode }}
                        </div>
                        @if (skuIdControl?.touched && skuIdControl?.invalid) {
                            <div class="small text-danger">Please select a value</div>
                        }
                    </div>
                    <div class="form-group">
                        <label for="voucherQuantity" class="form-label fw-semibold">Quantity</label>
                        <select
                            id="voucherQuantity"
                            class="form-select"
                            formControlName="quantity"
                            data-testid="voucher-quantity-input"
                            [class.is-invalid]="quantityControl?.invalid && quantityControl?.touched"
                        >
                            @for (quantity of quantityOptions; track quantity) {
                                <option [ngValue]="quantity">{{ quantity }}</option>
                            }
                        </select>
                    </div>
                </div>
            }
            @if (!voucher.isOnSale || !canPurchaseVoucher) {
                <div class="rounded-3 bg-warning-subtle border border-warning p-3 vstack gap-1">
                    <h6 class="fw-semibold mb-0">This voucher is not available to buy online right now</h6>
                    <p class="text-secondary mb-0">Please contact {{ venue.voucherDisplayName() }} directly for gift voucher availability.</p>
                </div>
            }
        </div>
        <div class="voucher-delivery vstack gap-1">
            <h6 class="fw-semibold">
                Delivery Options
            </h6>
            <div class="vstack gap-2">
                <div class="rounded-3 border bg-body p-3 vstack gap-1">
                    <div class="hstack gap-2 align-items-center">
                        <img
                            ngSrc="assets/mail/normal.svg"
                            width="20"
                            height="20"
                            alt=""
                            aria-hidden="true"
                        >
                        <span class="fw-semibold">
                            Email delivery
                        </span>
                    </div>
                    <p class="text-body-secondary mb-0">
                        Free delivery by email to you or your preferred recipient after checkout.
                    </p>
                </div>
                @if (voucher.postalDeliveryEnabled) {
                    <div class="rounded-3 border bg-body p-3 vstack gap-1" data-testid="voucher-postal-delivery">
                        <div class="hstack gap-2 align-items-center">
                            <img
                                ngSrc="assets/inbox/normal.svg"
                                width="20"
                                height="20"
                                alt=""
                                aria-hidden="true"
                            >
                            <span class="fw-semibold">
                                Postal delivery
                            </span>
                        </div>
                        <p class="text-body-secondary mb-0">
                            Available
                            @if ((voucher.postalDeliveryFee ?? 0) > 0) {
                                for {{ null | currencyUnit : postalDeliveryFee : venue.currencyCode }} per order
                            } @else {
                                at no extra cost
                            }
                            and can be sent to you, or your preferred recipient's postal address.
                        </p>
                    </div>
                }
            </div>
        </div>
    </div>
    <div class="voucher-sidebar">
        <div class="voucher-preview-section vstack gap-3">
            <h6 class="voucher-preview-heading fw-semibold mb-0">Voucher preview</h6>
            <div class="voucher-preview">
                @if (voucher.imageId) {
                    <img
                        class="voucher-image rounded-3"
                        [ngSrc]="environment.cdnUrl | thumbor : 'voucher-images/' + voucher.imageId : { width: 680, height: 428, aspectFit: true }"
                        [alt]="voucher.displayName || venue.voucherDisplayName()"
                        fill
                        sizes="(min-width: 992px) 24vw, 32vw"
                    />
                } @else {
                    <app-voucher-card [venue]="venue" [voucher]="voucher" [sku]="selectedSKU">
                    </app-voucher-card>
                }
            </div>
        </div>
        <div class="voucher-information">
            <div class="vstack gap-1">
                <ng-container *ngIf="expiryDate !== null">
                    <h6 class="fw-semibold">
                        Voucher Details
                    </h6>
                    @if (voucher.expiryMonths) {
                        <p data-testid="voucher-validity">
                            This voucher is valid for {{ voucher.expiryMonths }} month{{ voucher.expiryMonths > 1 ? 's' : '' }} from the date of purchase.
                        </p>
                    } @else {
                        <p data-testid="voucher-validity">
                            This voucher is valid until the end of {{ expiryDate | date }}.
                        </p>
                    }
                </ng-container>
                <div class="voucher-policy-section vstack gap-1">
                    @if (voucher.policy && voucher.policy.length) {
                        <label class="text-muted">
                            For terms and conditions of use, see below.
                        </label>
                        <div class="list-group">
                            <button
                                class="voucher-policy list-group-item list-group-item-action hstack justify-content-between text-start"
                                type="button"
                                (click)="showPolicy()"
                            >
                                <span class="fw-semibold">
                                    Terms & Conditions
                                </span>
                                <img
                                    ngSrc="assets/right/normal.svg"
                                    width="16"
                                    height="16"
                                    alt=""
                                    aria-hidden="true"
                                >
                            </button>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>
</div>

<ng-template #checkoutFooter>
    <div class="voucher-checkout-footer">
        <div class="voucher-total">
            <div class="text-body-secondary">Total</div>
            <div class="voucher-total-price fw-semibold" data-testid="voucher-total">
                @if (totalPrice !== null) {
                    {{ null | currencyUnit : totalPrice : venue.currencyCode }}
                } @else {
                    <span aria-label="Select a valid voucher and quantity">&mdash;</span>
                }
            </div>
        </div>
        <button
            type="button"
            class="voucher-checkout-button btn btn-primary"
            appVenueColour
            [primaryColour]="venue.primaryColour"
            [disabled]="!voucher.isOnSale || !canPurchaseVoucher || !selectedSKU || form.invalid"
            (click)="goToCheckout()"
        >
            Proceed to Checkout
        </button>
    </div>
</ng-template>
